"use client"

import React, { useMemo } from "react"

// Local component imports
import { Href } from "components/navigation"
import TimeValue from "compositions/TimeValue"
import { Icon, type IconName } from "components/ui"
import { OpponentLogo } from "components/dataDisplay"

// Local utility imports
import { getGameDateTime } from "helpers/getters"
import { formatTextToSlug } from "@/utils/helper"
import SXOutcomeButton from "@/compositions/OutcomeButton/SXOutcomeButton"

interface Market {
  marketHash: string
  baseToken: string
  bestOddsOutcomeOne: string | null
  bestOddsOutcomeTwo: string | null
  outcomeOneOdds: number | null
  outcomeTwoOdds: number | null
}

interface Game {
  gameLabel: string
  sportId: number
  leagueLabel: string
  sportsSlug: string
  sportsName: string
  betGroup: string
  marketType: string
  outcomeOneName: string
  outcomeTwoName: string
  providerMarketType: string
  gameTime: string // ISO string
  fixtureId: string
  market: Market
}

interface Order {
  key: string
  label: string
  odds: number | null
  order: Game
  outcome: "one" | "two"
}

const SXTopEventCard: React.FC<{ game: Game }> = ({ game }) => {
  const {
    betGroup,
    fixtureId,
    sportsSlug,
    leagueLabel,
    sportsName,
    gameTime,
    gameLabel,
    outcomeOneName,
    outcomeTwoName,
    market,
  } = game

  const flatOrders: Order[] = useMemo(
    () => [
      {
        key: `${market.marketHash}-one`,
        label: outcomeOneName,
        odds: market.outcomeTwoOdds,
        order: game,
        outcome: "one",
      },
      {
        key: `${market.marketHash}-two`,
        label: outcomeTwoName,
        odds: market.outcomeOneOdds,
        order: game,
        outcome: "two",
      },
    ],
    [
      market.marketHash,
      outcomeOneName,
      outcomeTwoName,
      market.outcomeOneOdds,
      market.outcomeTwoOdds,
      game,
    ]
  )

  // Memoize expensive operations
  const { date, time } = useMemo(
    () => getGameDateTime(new Date(gameTime).getTime()),
    [gameTime]
  )

  function buildGameUrl(
    sportsSlug: string,
    leagueLabel: string,
    fixtureId: string,
    betGroup: string
  ) {
    return `${sportsSlug}/${formatTextToSlug(leagueLabel)}/international/${fixtureId}/${betGroup}`
  }

  const now = new Date()
  const endOfTomorrow = new Date(now)
  endOfTomorrow.setDate(now.getDate() + 1)
  endOfTomorrow.setHours(23, 59, 59, 999)
  const endOfTomorrowTime = endOfTomorrow.getTime()

  const isLocked = +new Date(gameTime).getTime() > endOfTomorrowTime

  return (
    <div className="bg-card-border-bottom p-px rounded-md overflow-hidden">
      <div className="bg-gradient-event-card p-4 rounded-md">
        <Header
          sportsSlug={sportsSlug}
          sportsName={sportsName}
          leagueLabel={leagueLabel}
          gameUrl={buildGameUrl(sportsSlug, leagueLabel, fixtureId, betGroup)}
        />
        <div className="mt-3 flex items-center justify-between px-7">
          <OpponentLogo image="interface/no_opponent" size={48} />
          <div className="text-caption-12 text-center">
            <div className="text-accent-grey-200">{date}</div>
            <div className="font-semibold mt-[2px]">
              <TimeValue time={time} />
            </div>
          </div>
          <OpponentLogo image="interface/no_opponent" size={48} />
        </div>

        <div className="mt-5 text-caption-13 font-semibold text-center text-ellipsis whitespace-nowrap overflow-hidden">
          {gameLabel}
        </div>

        <div className="mt-3 flex items-center space-x-2">
          {flatOrders.map(order => (
            <SXOutcomeButton
              key={order.key}
              marketName={order.order.marketType}
              order={order}
              game={game as any}
              isLocked={isLocked || !order.odds}
              size={40}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

const Header = React.memo(
  ({
    sportsSlug,
    sportsName,
    leagueLabel,
    gameUrl,
  }: {
    sportsSlug: string
    sportsName: string
    leagueLabel: string
    gameUrl: string
  }) => (
    <Href
      to={gameUrl}
      className="flex items-center justify-center text-accent-grey-200 text-caption-13 hover:underline"
    >
      <Icon
        className="size-4 mr-2 flex-none"
        name={`sport/${sportsSlug}` as IconName}
      />
      <span className="text-ellipsis whitespace-nowrap overflow-hidden">
        {sportsName}
      </span>
      <div className="size-[2px] rounded-full bg-accent-grey-700 mx-1" />
      <span className="text-ellipsis whitespace-nowrap overflow-hidden">
        {leagueLabel}
      </span>
    </Href>
  )
)

export default SXTopEventCard
